import React, { memo, useCallback, useMemo, useState } from 'react'
import ApolloErrorMessages from 'transfix-ui/components/ApolloErrorMessages'
import {
  ConfirmationModal,
  IConfirmationModalProps,
} from 'transfix-ui/components/Modals/ConfirmationModal'
import { IModalAction } from 'transfix-ui/components/Modals/ModalAction'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { IError } from 'types/graphqlTypes'
import { useMPAnalytics } from 'apps/marketplace/hooks/useMPAnalytics'
import { useMarkOptionAsAvailableMutation } from './graphql/MarkOptionAsAvailableMutation'
import { useMarkOptionAsUnavailableMutation } from './graphql/MarkOptionAsUnavailableMutation'

interface IAvailabilityConfirmationModalProps extends Pick<IConfirmationModalProps, 'onClose'> {
  optionId: string
  isAvailable: boolean
  onSuccess: () => void
}

const AvailabilityConfirmationModal = memo<IAvailabilityConfirmationModalProps>(
  ({ isAvailable, onClose, onSuccess, optionId }) => {
    const { trackEvent } = useMPAnalytics()
    const [mutationErrors, setMutationErrors] = useState<IError[]>([])

    const { openToast } = useGlobalToast()
    const [
      deactivateOption,
      { error: markAsUnavailableErrors, loading: unavailableLoading },
    ] = useMarkOptionAsUnavailableMutation()
    const [
      reactivateOption,
      { error: markAsAvailableErrors, loading: availableLoading },
    ] = useMarkOptionAsAvailableMutation()

    const loading = unavailableLoading || availableLoading

    const showSuccessToast = useCallback(() => {
      openToast({
        toastMessage: `Option marked as ${isAvailable ? 'unavailable' : 'available'}.`,
        toastType: 'default',
      })
    }, [isAvailable, openToast])

    const markOptionAsUnavailable = useCallback(async () => {
      const { data } = await deactivateOption({
        variables: {
          input: {
            id: optionId,
          },
        },
      })
      const { declineBid } = data || {}
      const { bid, errors = [] } = declineBid || {}
      const { id: bidId } = bid || {}

      if (errors.length) {
        trackEvent('Carrier Options', 'ERROR_DECLINING_OPTION', {
          carrierOptionId: optionId,
          errors,
        })

        setMutationErrors(errors)
        return
      }

      if (bidId) {
        trackEvent('Carrier Options', 'DECLINED_OPTION', { carrierOptionId: bidId })
        showSuccessToast()
        onSuccess()
      }
    }, [deactivateOption, onSuccess, optionId, showSuccessToast, trackEvent])

    const markOptionAsAvailable = useCallback(async () => {
      const { data } = await reactivateOption({
        variables: {
          input: {
            id: optionId,
          },
        },
      })
      const { reactivateDeclinedBid } = data || {}
      const { bid, errors = [] } = reactivateDeclinedBid || {}
      const { id } = bid || {}

      if (errors.length) {
        trackEvent('Carrier Options', 'ERROR_REACTIVATING_OPTION', {
          carrierOptionId: optionId,
          errors,
        })
        setMutationErrors(errors)
        return
      }

      if (id) {
        trackEvent('Carrier Options', 'REACTIVATED_OPTION', { carrierOptionId: optionId })
        showSuccessToast()
        onSuccess()
      }
    }, [onSuccess, optionId, reactivateOption, showSuccessToast, trackEvent])

    const availabilityToggleHandler = useCallback(() => {
      setMutationErrors([])
      if (isAvailable) {
        markOptionAsUnavailable()
      } else {
        markOptionAsAvailable()
      }
    }, [isAvailable, markOptionAsAvailable, markOptionAsUnavailable])

    const modalActions: IModalAction[] = useMemo(
      () => [
        {
          label: 'Cancel',
          type: 'dismiss',
          disable: loading,
          action: onClose,
        },
        {
          label: `Mark as ${isAvailable ? `Unavailable` : `Available`}`,
          type: 'confirm',
          disable: loading,
          action: availabilityToggleHandler,
        },
      ],
      [availabilityToggleHandler, isAvailable, loading, onClose]
    )

    return (
      <ConfirmationModal
        actions={modalActions}
        onClose={onClose}
        title={`Are you sure that you want to mark this carrier option '${
          isAvailable ? `unavailable` : `available`
        }'?`}
        titleSize={4}
        open>
        <ApolloErrorMessages
          mutationErrors={mutationErrors}
          apolloErrors={markAsUnavailableErrors || markAsAvailableErrors}
        />
      </ConfirmationModal>
    )
  }
)

export default AvailabilityConfirmationModal
